"use client";

import { useQuery } from "@tanstack/react-query";
import { API_BASE } from "./api";
import { useSession } from "./useSignIn";

export interface LedgerBalance {
  currency: "USD" | "NGN";
  balance: string;
  held: string;
  available: string;
}

/** Signed-in user's ledger balances. Empty until authenticated. */
export const useBalances = () => {
  const { isSignedIn } = useSession();

  const q = useQuery({
    queryKey: ["balances"],
    enabled: isSignedIn,
    queryFn: async (): Promise<LedgerBalance[]> => {
      const res = await fetch(`${API_BASE}/pro/balances`, { credentials: "include" });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.success) throw new Error(body?.message || "Failed to load balances");
      return body.data.balances as LedgerBalance[];
    },
    // 8s: fills settle via the server's sync loop, so balances change without any user
    // action, a long poll here reads as "my money is missing" (seen in e2e: a filled buy
    // showed $0.00 for up to 15s). Cheap query against our own DB.
    refetchInterval: 8_000,
  });

  const find = (c: "USD" | "NGN") => q.data?.find((b) => b.currency === c);
  return {
    balances: q.data ?? [],
    usd: find("USD"),
    ngn: find("NGN"),
    isLoading: q.isLoading,
  };
};
